import { fileExtension } from '../mediaViewer/mediaFileTypes';

export type FileOpenSurface =
  | 'zip'
  | 'media'
  | 'pdf'
  | 'spreadsheet'
  | 'richedit'
  | 'drawio'
  | 'markdown'
  | 'monaco'
  | 'code'
  | 'none';

/** Archive types — double-click shows extract placeholder (P4). */
export const ZIP_EXTENSIONS = new Set(['zip', '7z', 'rar', 'tar', 'gz', 'tgz', 'bz2', 'xz']);

/** Inline-previewable images. SVG is intentionally excluded (XSS). */
export const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'ico',
  'avif',
  'heic',
  'heif'
]);

export const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv']);

export const PDF_EXTENSIONS = new Set(['pdf']);

/** Excel-family extensions editable/previewable in the DevExpress Spreadsheet control. */
export const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls', 'xlsm', 'csv', 'tsv']);

/** Word-family extensions editable/previewable in the DevExpress RichEdit control. */
export const RICHEDIT_EXTENSIONS = new Set(['docx', 'doc', 'rtf']);

export const DRAWIO_EXTENSIONS = new Set(['drawio', 'dio']);

export const MARKDOWN_EXTENSIONS = new Set(['md', 'markdown', 'jdoc']);

/**
 * Plain text extensions opened in the Text Editor (Monaco shell).
 * Code/config types use {@link CODE_EXTENSIONS} / Code Editor instead.
 */
export const MONACO_EXTENSIONS = new Set(['txt', 'jenc', 'log', 'env']);

/**
 * Code and config extensions opened in the Code Editor.
 * Markdown and office types are handled by higher-priority surfaces.
 */
export const CODE_EXTENSIONS = new Set([
  'json',
  'jsonc',
  'xml',
  'yaml',
  'yml',
  'ps1',
  'cs',
  'sql',
  'js',
  'mjs',
  'cjs',
  'ts',
  'tsx',
  'jsx',
  'css',
  'scss',
  'less',
  'html',
  'htm',
  'sh',
  'bash',
  'bat',
  'cmd',
  'ini',
  'cfg',
  'conf',
  'toml',
  'go',
  'rs',
  'rb',
  'php',
  'java',
  'kt',
  'swift',
  'vue',
  'svelte',
  'graphql',
  'gql',
  'h',
  'hpp',
  'c',
  'cpp',
  'cc',
  'csx',
  'vb',
  'fs',
  'fsx',
  'dockerfile',
  'makefile',
  'cmake',
  'proto',
  'prisma',
  'gitignore',
  'gitattributes',
  'editorconfig',
  'npmrc',
  'prettierrc',
  'eslintrc',
  'py'
]);

/** Extensions that must not open in any inline preview surface. */
export const EXCLUDED_EXTENSIONS = new Set(['svg']);

export type NewFileStarterCategory = 'office' | 'text' | 'markdown' | 'diagram' | 'data' | 'code';

export type NewFileStarter = {
  id: string;
  extension: string;
  label: string;
  category: NewFileStarterCategory;
  defaultName: string;
  mimeType: string;
  /** UTF-8 starter body (upload API rejects 0-byte files). */
  blankContent: string;
  openSurface: FileOpenSurface;
  icon?: string;
  /** When true, creation is offered in the menu but not fully implemented yet. */
  stub?: boolean;
};

export const NEW_FILE_STARTERS: readonly NewFileStarter[] = [
  {
    id: 'xlsx',
    extension: '.xlsx',
    label: 'Excel workbook',
    category: 'office',
    defaultName: 'Workbook.xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    blankContent: '',
    openSurface: 'spreadsheet',
    icon: 'xlsxfile'
  },
  {
    id: 'docx',
    extension: '.docx',
    label: 'Word document',
    category: 'office',
    defaultName: 'Document.docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    blankContent: '',
    openSurface: 'richedit',
    icon: 'docxfile'
  },
  {
    id: 'txt',
    extension: '.txt',
    label: 'Text file',
    category: 'text',
    defaultName: 'Notes.txt',
    mimeType: 'text/plain',
    blankContent: '\n',
    openSurface: 'monaco'
  },
  {
    id: 'jenc',
    extension: '.jenc',
    label: 'Encrypted text',
    category: 'text',
    defaultName: 'Secret.jenc',
    mimeType: 'application/octet-stream',
    blankContent: '\n',
    openSurface: 'monaco'
  },
  {
    id: 'log',
    extension: '.log',
    label: 'Log file',
    category: 'text',
    defaultName: 'app.log',
    mimeType: 'text/plain',
    blankContent: '\n',
    openSurface: 'monaco'
  },
  {
    id: 'env',
    extension: '.env',
    label: 'Env file',
    category: 'text',
    defaultName: 'app.env',
    mimeType: 'text/plain',
    blankContent: '# \n',
    openSurface: 'monaco'
  },
  {
    id: 'md',
    extension: '.md',
    label: 'Markdown',
    category: 'markdown',
    defaultName: 'Notes.md',
    mimeType: 'text/markdown',
    blankContent: '# \n',
    openSurface: 'markdown'
  },
  {
    id: 'jdoc',
    extension: '.jdoc',
    label: 'JDoc document',
    category: 'markdown',
    defaultName: 'Document.jdoc',
    mimeType: 'application/json',
    blankContent: '{}\n',
    openSurface: 'markdown'
  },
  {
    id: 'drawio',
    extension: '.drawio',
    label: 'Draw.io diagram',
    category: 'diagram',
    defaultName: 'Diagram.drawio',
    mimeType: 'application/xml',
    blankContent: '<mxfile><diagram></diagram></mxfile>\n',
    openSurface: 'drawio'
  },
  {
    id: 'jsheet',
    extension: '.jsheet',
    label: 'JSheet workbook',
    category: 'data',
    defaultName: 'Workbook.jsheet',
    mimeType: 'application/json',
    blankContent: '{}\n',
    openSurface: 'spreadsheet',
    stub: true
  },
  {
    id: 'csv',
    extension: '.csv',
    label: 'CSV spreadsheet',
    category: 'data',
    defaultName: 'Data.csv',
    mimeType: 'text/csv',
    blankContent: 'column1\n',
    openSurface: 'spreadsheet'
  },
  {
    id: 'json',
    extension: '.json',
    label: 'JSON',
    category: 'code',
    defaultName: 'data.json',
    mimeType: 'application/json',
    blankContent: '{}\n',
    openSurface: 'code'
  },
  {
    id: 'jsonc',
    extension: '.jsonc',
    label: 'JSONC',
    category: 'code',
    defaultName: 'settings.jsonc',
    mimeType: 'application/json',
    blankContent: '{\n  // \n}\n',
    openSurface: 'code'
  },
  {
    id: 'xml',
    extension: '.xml',
    label: 'XML',
    category: 'code',
    defaultName: 'document.xml',
    mimeType: 'application/xml',
    blankContent: '<?xml version="1.0" encoding="UTF-8"?>\n',
    openSurface: 'code'
  },
  {
    id: 'yaml',
    extension: '.yaml',
    label: 'YAML',
    category: 'code',
    defaultName: 'config.yaml',
    mimeType: 'application/yaml',
    blankContent: '# \n',
    openSurface: 'code'
  },
  {
    id: 'js',
    extension: '.js',
    label: 'JavaScript',
    category: 'code',
    defaultName: 'script.js',
    mimeType: 'text/javascript',
    blankContent: '// \n',
    openSurface: 'code'
  },
  {
    id: 'ts',
    extension: '.ts',
    label: 'TypeScript',
    category: 'code',
    defaultName: 'script.ts',
    mimeType: 'text/typescript',
    blankContent: '// \n',
    openSurface: 'code'
  },
  {
    id: 'tsx',
    extension: '.tsx',
    label: 'TSX',
    category: 'code',
    defaultName: 'Component.tsx',
    mimeType: 'text/typescript',
    blankContent: 'export function Component() {\n  return null;\n}\n',
    openSurface: 'code'
  },
  {
    id: 'py',
    extension: '.py',
    label: 'Python',
    category: 'code',
    defaultName: 'script.py',
    mimeType: 'text/x-python',
    blankContent: '# \n',
    openSurface: 'code'
  },
  {
    id: 'html',
    extension: '.html',
    label: 'HTML',
    category: 'code',
    defaultName: 'index.html',
    mimeType: 'text/html',
    blankContent: '<!DOCTYPE html>\n<html>\n<head>\n  <title></title>\n</head>\n<body>\n</body>\n</html>\n',
    openSurface: 'code'
  },
  {
    id: 'css',
    extension: '.css',
    label: 'CSS',
    category: 'code',
    defaultName: 'styles.css',
    mimeType: 'text/css',
    blankContent: '/* */\n',
    openSurface: 'code'
  },
  {
    id: 'sh',
    extension: '.sh',
    label: 'Shell script',
    category: 'code',
    defaultName: 'script.sh',
    mimeType: 'text/x-shellscript',
    blankContent: '#!/usr/bin/env bash\n',
    openSurface: 'code'
  },
  {
    id: 'ps1',
    extension: '.ps1',
    label: 'PowerShell script',
    category: 'code',
    defaultName: 'script.ps1',
    mimeType: 'text/plain',
    blankContent: '# \n',
    openSurface: 'code'
  },
  {
    id: 'cs',
    extension: '.cs',
    label: 'C# source',
    category: 'code',
    defaultName: 'Program.cs',
    mimeType: 'text/plain',
    blankContent: '// \n',
    openSurface: 'code'
  },
  {
    id: 'sql',
    extension: '.sql',
    label: 'SQL script',
    category: 'code',
    defaultName: 'query.sql',
    mimeType: 'application/sql',
    blankContent: '-- \n',
    openSurface: 'code'
  },
  {
    id: 'ini',
    extension: '.ini',
    label: 'Config file',
    category: 'code',
    defaultName: 'config.ini',
    mimeType: 'text/plain',
    blankContent: '; \n',
    openSurface: 'code'
  },
  {
    id: 'dockerfile',
    extension: '.dockerfile',
    label: 'Dockerfile',
    category: 'code',
    defaultName: 'Dockerfile.dockerfile',
    mimeType: 'text/plain',
    blankContent: 'FROM scratch\n',
    openSurface: 'code'
  }
];

const SURFACE_EXTENSION_SETS: ReadonlyArray<{ surface: FileOpenSurface; extensions: ReadonlySet<string> }> = [
  { surface: 'zip', extensions: ZIP_EXTENSIONS },
  { surface: 'media', extensions: new Set([...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS]) },
  { surface: 'pdf', extensions: PDF_EXTENSIONS },
  { surface: 'spreadsheet', extensions: SPREADSHEET_EXTENSIONS },
  { surface: 'richedit', extensions: RICHEDIT_EXTENSIONS },
  { surface: 'drawio', extensions: DRAWIO_EXTENSIONS },
  { surface: 'markdown', extensions: MARKDOWN_EXTENSIONS },
  { surface: 'code', extensions: CODE_EXTENSIONS },
  { surface: 'monaco', extensions: MONACO_EXTENSIONS }
];

/** Every extension registered in a surface set and its resolved surface (for tests). */
export function getRegisteredExtensionSurfaces(): ReadonlyMap<string, FileOpenSurface> {
  const map = new Map<string, FileOpenSurface>();
  for (const { surface, extensions } of SURFACE_EXTENSION_SETS) {
    for (const ext of extensions) {
      map.set(ext, surface);
    }
  }
  for (const ext of EXCLUDED_EXTENSIONS) {
    map.set(ext, 'none');
  }
  return map;
}

/** Extensionless / special basenames opened in the Code Editor. */
const CODE_BASENAMES = new Set([
  'dockerfile',
  'makefile',
  'gnumakefile',
  '.gitignore',
  '.gitattributes',
  '.editorconfig',
  '.npmrc',
  '.prettierrc',
  '.eslintrc'
]);

function fileBasename(fileName: string): string {
  const seg = fileName.replace(/\\/g, '/').split('/').filter(Boolean).pop();
  return seg ?? fileName;
}

export function resolveOpenSurface(fileName: string): FileOpenSurface {
  const base = fileBasename(fileName);
  if (CODE_BASENAMES.has(base.toLowerCase())) return 'code';

  const ext = fileExtension(fileName);
  if (!ext || EXCLUDED_EXTENSIONS.has(ext)) return 'none';
  if (ZIP_EXTENSIONS.has(ext)) return 'zip';
  if (IMAGE_EXTENSIONS.has(ext) || VIDEO_EXTENSIONS.has(ext)) return 'media';
  if (PDF_EXTENSIONS.has(ext)) return 'pdf';
  if (SPREADSHEET_EXTENSIONS.has(ext)) return 'spreadsheet';
  if (RICHEDIT_EXTENSIONS.has(ext)) return 'richedit';
  if (DRAWIO_EXTENSIONS.has(ext)) return 'drawio';
  if (MARKDOWN_EXTENSIONS.has(ext)) return 'markdown';
  if (CODE_EXTENSIONS.has(ext)) return 'code';
  if (MONACO_EXTENSIONS.has(ext)) return 'monaco';
  return 'none';
}

export function isTextEditorFile(fileName: string): boolean {
  return resolveOpenSurface(fileName) === 'monaco';
}

export function isCodeEditorFile(fileName: string): boolean {
  return resolveOpenSurface(fileName) === 'code';
}

export function isMarkdownFile(fileName: string): boolean {
  return resolveOpenSurface(fileName) === 'markdown';
}

export function isDrawioFile(fileName: string): boolean {
  return resolveOpenSurface(fileName) === 'drawio';
}

export function isZipFile(fileName: string): boolean {
  return resolveOpenSurface(fileName) === 'zip';
}

export function isMediaFile(fileName: string): boolean {
  return resolveOpenSurface(fileName) === 'media';
}

export function getNewFileStarter(id: string): NewFileStarter | undefined {
  return NEW_FILE_STARTERS.find((starter) => starter.id === id);
}

const CATEGORY_LABELS: Record<NewFileStarterCategory, string> = {
  office: 'Office',
  text: 'Text',
  markdown: 'Markdown',
  diagram: 'Diagram',
  data: 'Data',
  code: 'Code'
};

export type NewFileMenuItem =
  | { id: string; text: string; icon?: string; disabled?: boolean }
  | { id: `category-${NewFileStarterCategory}`; text: string; disabled: true };

/** Builds grouped New File dropdown items from {@link NEW_FILE_STARTERS}. */
export function buildNewFileMenuItemsFromRegistry(): NewFileMenuItem[] {
  const items: NewFileMenuItem[] = [];
  let lastCategory: NewFileStarterCategory | null = null;

  for (const starter of NEW_FILE_STARTERS) {
    if (starter.category !== lastCategory) {
      items.push({
        id: `category-${starter.category}`,
        text: CATEGORY_LABELS[starter.category],
        disabled: true
      });
      lastCategory = starter.category;
    }
    items.push({
      id: starter.id,
      text: starter.stub ? `${starter.label} (coming soon)` : starter.label,
      icon: starter.icon,
      disabled: starter.stub
    });
  }

  return items;
}

export type EditorPlaceholderSurface = 'monaco' | 'code' | 'markdown' | 'drawio';

const PLACEHOLDER_ROUTES: Record<EditorPlaceholderSurface, string> = {
  monaco: '/files/text-editor',
  code: '/files/code-editor',
  markdown: '/files/markdown-editor',
  drawio: '/files/drawio-editor'
};

/** Placeholder SPA routes for editors not yet implemented. */
export function editorPlaceholderHref(
  surface: EditorPlaceholderSurface,
  mountId: string,
  relativePath: string
): string {
  const params = new URLSearchParams();
  params.set('mountId', mountId);
  params.set('path', relativePath);
  return `${PLACEHOLDER_ROUTES[surface]}?${params.toString()}`;
}
